import React, { useEffect, useMemo, useRef, useState } from 'react';
import RunControlPanel, { derivePhase, isLive, Progress } from './RunControlPanel';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import '@xterm/xterm/css/xterm.css';
import { authUtils } from '../../services/auth';
import { apiService } from '../../services/api';
import config from '../../config/environment';
import './LiveTerminals.css';

/**
 * Live terminals
 *
 * Streams the practice-run terminal lanes for a competition. The card is on the
 * page whenever the competition supports a practice sandbox at all -- it does
 * not wait for a job, because a competitor opening the page must see the four
 * terminals immediately, empty if need be.
 *
 * It polls GET /evaluations/{cid}/practice/terminals on an adaptive cadence:
 * 1.5s while lanes are live, 2s while the sandbox is starting, 5s when a run
 * may be starting elsewhere, 15s at rest -- and paused entirely while the tab
 * is hidden. A status pill reflects the pulsar state; a two-click Cancel
 * aborts an in-flight run.
 *
 * Runs execute in PARALLEL, all N at once, so at any moment there can be
 * 4 x N live lanes; the grid shows the four tasks of ONE run and the arrows
 * move between runs. Feeds are consumed with fetch + ReadableStream
 * (EventSource cannot send an Authorization header), SSE frames parsed by hand.
 */

type StreamStatus = 'running' | 'done';

interface TerminalStream {
  task_id: string;
  run: number;
  status: StreamStatus;
  bytes: number;
}

/** Per-lane token counters, sampled server-side on one clock so the visible
 *  cells always add up to the total shown beside them. */
interface Usage {
  requests: number;
  /** NEW tokens. An agent resends its whole conversation every turn, so the
   *  raw prompt total counts the same text once per turn -- 26 calls billed
   *  1.4M against a 210k context. This is the number people mean. */
  input_tokens: number;
  output_tokens: number;
  /** What the server actually charged, kept for cost accounting. */
  billed_input_tokens: number;
}

interface UsageSnapshot {
  /** Keyed "<task_id>/<run>". */
  per_lane: Record<string, Usage>;
  total: Usage;
  at: number;
}

interface TerminalsResponse {
  /** True for competitions that have a practice sandbox at all. */
  supported: boolean;
  /** Set ONLY for a practice run, i.e. only when live terminals exist. */
  job_id: string | null;
  /** The caller's latest run of any kind. Graded runs appear here and not above. */
  run: { job_id: string; kind: string; state: string | null } | null;
  state: string | null;
  available: boolean;
  n_runs: number;
  streams: TerminalStream[];
  usage: UsageSnapshot | null;
  /** Lane states, timing and token counters. Present for GRADED runs too:
   *  it carries no agent output, so it is safe on the held-out world. */
  progress: Progress | null;
  queue_position: number | null;
  queue_length: number;
  queue_head_age_seconds: number | null;
}

const TASKS: { id: string; label: string }[] = [
  { id: '01_ticket_intake', label: 'Task 1: Ticket intake' },
  { id: '02_status_chasing', label: 'Task 2: Status chasing' },
  { id: '03_board_hygiene', label: 'Task 3: Board hygiene' },
  { id: '04_weekly_report', label: 'Task 4: Weekly report' },
];

// Adaptive listing-poll cadence. We always fetch once immediately, then pick the
// next delay from the job's freshest state (see computeDelay below).
const POLL_STARTING_MS = 2000; // job known but sandbox/lanes not ready yet
const POLL_LIVE_MS = 1500;     // available + running: token counters feel live
const POLL_DISCOVER_MS = 5000; // a run may be starting: find it quickly
// Nothing is happening and nobody has asked for anything. The card is on every
// competition page for every logged-in user, so its resting cost matters more
// than its latency; a run started elsewhere still shows up within this.
const POLL_IDLE_MS = 15000;
const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_TRIES = 5;
// After the budget: keep trying, just quietly. A live run must never be
// unrecoverable without a page reload.
const RECONNECT_SLOW_MS = 8000;
// A 404 means "this lane's run hasn't opened yet", which is normal while the
// sandbox boots and between runs. It must NOT consume the reconnect budget:
// five transient 404s used to latch "connection lost" permanently.
const WAIT_RETRY_MS = 4000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Transcripts already streamed, keyed "job|task|run".
 *
 * A cell is keyed by (job, task, run), so switching runs unmounts it and
 * disposes its xterm buffer -- and once the job is over the pod is gone, so
 * remounting has nothing to re-fetch and you get an empty pane. Keeping the
 * bytes here makes the run arrows work after a run finishes.
 *
 * In memory, not localStorage: transcripts run to megabytes and would blow the
 * ~5MB quota on the first real run. Lost on refresh, which is the honest
 * trade -- by then the pod is gone anyway and the full logs are downloadable.
 */
interface Transcript {
  chunks: Uint8Array[];
  /** Running total. Re-reducing the whole array per chunk made buffering
   *  O(n^2) in chunk count, on the main thread, while four terminals stream. */
  bytes: number;
}

const TRANSCRIPTS = new Map<string, Transcript>();
const TRANSCRIPT_MAX_BYTES = 4 * 1024 * 1024;

const transcriptKey = (jobId: string | null, taskId: string, run: number) =>
  `${jobId ?? 'nojob'}|${taskId}|${run}`;

const rememberChunk = (key: string, bytes: Uint8Array) => {
  const entry = TRANSCRIPTS.get(key) ?? { chunks: [], bytes: 0 };
  entry.chunks.push(bytes);
  entry.bytes += bytes.length;
  // Drop from the front rather than refuse to record: the tail is what someone
  // scrolling back after the fact actually wants.
  while (entry.bytes > TRANSCRIPT_MAX_BYTES && entry.chunks.length > 1) {
    entry.bytes -= (entry.chunks.shift() as Uint8Array).length;
  }
  TRANSCRIPTS.set(key, entry);
};

/** Forget everything cached for other jobs. The cap is per key, so without
 *  this the map only ever grows across a long SPA session. */
const forgetOtherJobs = (jobId: string | null) => {
  const keep = `${jobId ?? 'nojob'}|`;
  TRANSCRIPTS.forEach((_, key) => {
    if (!key.startsWith(keep)) TRANSCRIPTS.delete(key);
  });
};

/** 1234 -> "1.2k". Terminal headers are narrow and the exact figure is in the
 *  title attribute; what matters at a glance is the order of magnitude. */
const fmtTokens = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

/** Search hits in the brand orange, the one being shown in full strength. */
const MATCH_COLORS = {
  matchBackground: '#7a3414', // theme-ok: terminal
  matchOverviewRuler: '#7a3414', // theme-ok: terminal
  activeMatchBackground: '#FC5715', // theme-ok: terminal
  activeMatchColorOverviewRuler: '#FC5715', // theme-ok: terminal
};

interface TerminalCellProps {
  competitionId: number;
  jobId: string | null;
  taskId: string;
  run: number;
  label: string;
  /** Stream status from the latest poll, or undefined when no stream exists yet. */
  streamStatus?: StreamStatus;
  /** True once the job reached a terminal state: stop retrying, keep the buffer. */
  jobTerminal?: boolean;
  /** This lane's token counters, or undefined before the first sample. */
  usage?: Usage;
  /** Picked in the run panel's lane grid: the pane gets an accent outline so a
   *  click up there is visibly answered down here. */
  selected?: boolean;
  /** Shown maximized. One lane at a time, so the parent holds which. */
  expanded: boolean;
  onExpand: (open: boolean) => void;
  /** Left/right arrow while maximized: maximize the neighbouring lane instead. */
  onMove: (step: number) => void;
}

const TerminalCell: React.FC<TerminalCellProps> = ({
  competitionId,
  jobId,
  taskId,
  run,
  label,
  selected,
  streamStatus,
  jobTerminal,
  usage,
  expanded,
  onExpand,
  onMove,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const searchRef = useRef<SearchAddon | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const startedRef = useRef(false);
  const endedRef = useRef(false);
  const disposedRef = useRef(false);
  const jobTerminalRef = useRef(Boolean(jobTerminal));

  // 'waiting' until a stream exists, then 'running' while the feed is live and
  // 'done' once the backend sends `event: end`.
  const [dotStatus, setDotStatus] = useState<'waiting' | 'running' | 'done'>(
    streamStatus ? 'running' : 'waiting'
  );
  const [reconnecting, setReconnecting] = useState(false);
  // Set on the first successful connect. Gates the placeholder overlay: once a
  // cell has streamed it must never be covered again -- when the pod dies the
  // listing returns `streams: []`, and the old `!streamStatus` test dropped the
  // absolutely-positioned overlay straight back over a finished transcript.
  const [hasStreamed, setHasStreamed] = useState(false);
  // New output arrived while the reader was scrolled up, so it is out of sight.
  const [behind, setBehind] = useState(false);
  // Search box in the expanded view: null while closed, else the query and its "3/12" result count.
  const [search, setSearch] = useState<string | null>(null);
  const [hits, setHits] = useState<{ index: number; count: number } | null>(null);

  useEffect(() => {
    jobTerminalRef.current = Boolean(jobTerminal);
  }, [jobTerminal]);

  // Create the terminal on mount, dispose on unmount. Reset the lifecycle flags
  // here so a remount (including React StrictMode's dev double-invoke) starts clean.
  useEffect(() => {
    if (!containerRef.current) return;
    disposedRef.current = false;
    startedRef.current = false;
    endedRef.current = false;
    const term = new Terminal({
      disableStdin: true,
      cursorBlink: false,
      convertEol: false,
      fontSize: 12,
      // Effectively unlimited: keep the whole run's output scrollable.
      // (xterm allocates scrollback lazily, so this costs nothing until used.)
      scrollback: 1000000,
      allowProposedApi: true, // search highlights are xterm decorations
      theme: {
        background: '#191919', // theme-ok: terminal
        foreground: '#e5e7eb', // theme-ok: terminal
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    const searchAddon = new SearchAddon();
    term.loadAddon(searchAddon);
    const searched = searchAddon.onDidChangeResults(({ resultIndex, resultCount }) =>
      setHits({ index: resultIndex, count: resultCount }));
    searchRef.current = searchAddon;
    term.open(containerRef.current);
    // xterm draws no cursor until a terminal is focused or typed in, and these never are.
    const core = (term as unknown as { _core?: { coreService?: { isCursorInitialized: boolean } } })._core;
    if (core?.coreService) core.coreService.isCursorInitialized = true;
    try {
      fit.fit();
    } catch {
      /* container may not be laid out yet */
    }
    termRef.current = term;
    fitRef.current = fit;

    // Paint a local prompt immediately so the cell reads as a terminal waiting
    // for work rather than a dead black rectangle. The server replays its own
    // backlog (including the sandbox boot lines) on connect and resets first,
    // so this is discarded the moment real output arrives.
    term.write(`\x1b[2m-- waiting for lane --\x1b[0m\r\nagent@sandbox:~/${taskId}$ `);

    // ResizeObserver, not window.resize: expanding a cell toggles a class on
    // this node, which fires no window resize event, so a window-only listener
    // would leave an expanded terminal rendered at its old size.
    const refit = () => {
      try {
        fitRef.current?.fit();
      } catch {
        /* ignore fit errors during teardown */
      }
    };
    const observer = new ResizeObserver(() => window.requestAnimationFrame(refit));
    observer.observe(containerRef.current);
    window.addEventListener('resize', refit);

    // xterm keeps following the output only while the view is at the bottom; once the reader scrolls up, say when more arrives.
    const atBottom = () => term.buffer.active.viewportY >= term.buffer.active.baseY;
    const written = term.onWriteParsed(() => setBehind(!atBottom()));
    const viewport = containerRef.current.querySelector('.xterm-viewport');
    const onScroll = () => { if (atBottom()) setBehind(false); };
    viewport?.addEventListener('scroll', onScroll);

    return () => {
      disposedRef.current = true;
      observer.disconnect();
      window.removeEventListener('resize', refit);
      written.dispose();
      searched.dispose();
      viewport?.removeEventListener('scroll', onScroll);
      abortRef.current?.abort();
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
      searchRef.current = null;
    };
    // taskId is stable for the life of a cell (it is part of the React key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape leaves fullscreen, the arrows step to the next lane, Ctrl/Cmd+F searches this lane instead of the page.
  // Captured, because a clicked terminal swallows Escape and the arrows; the search box handles its own keys.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'Escape') onExpand(false);
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') onMove(e.key === 'ArrowLeft' ? -1 : 1);
      else if (e.key === 'f' && (e.metaKey || e.ctrlKey)) setSearch((q) => q ?? '');
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [expanded, onExpand, onMove]);

  // Collapsing closes the search and clears its highlights.
  useEffect(() => {
    if (expanded) return;
    setSearch(null);
    setHits(null);
    searchRef.current?.clearDecorations();
    termRef.current?.clearSelection();
  }, [expanded]);

  const find = (query: string, backwards = false) => {
    if (!query) {
      searchRef.current?.clearDecorations();
      setHits(null);
      return;
    }
    const options = { incremental: !backwards, decorations: MATCH_COLORS };
    if (backwards) searchRef.current?.findPrevious(query, options);
    else searchRef.current?.findNext(query, options);
  };

  // Start streaming immediately, without waiting for the listing to report a
  // stream. The runner pre-opens run 1 at boot, so the happy path connects at
  // once; and because a 404 is now treated as "not open yet" rather than a
  // failure, connecting early costs nothing and makes the cell robust to a
  // stale or unavailable listing. The only case worth skipping is a job that
  // was already over before this cell mounted.
  useEffect(() => {
    if (startedRef.current) return;
    // Nothing live to connect to, but we streamed this lane earlier in the
    // session: replay it so the run arrows still show something.
    const cached = TRANSCRIPTS.get(transcriptKey(jobId, taskId, run));
    if (!streamStatus && jobTerminal) {
      if (cached?.chunks.length && termRef.current) {
        termRef.current.reset();
        cached.chunks.forEach((c) => termRef.current?.write(c));
        setHasStreamed(true);
        setDotStatus('done');
        endedRef.current = true;
      }
      return;
    }
    startedRef.current = true;

    const writeBytes = (b64: string) => {
      const term = termRef.current;
      if (!term) return;
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      term.write(bytes);
      rememberChunk(transcriptKey(jobId, taskId, run), bytes);
    };

    const processFrame = (frame: string) => {
      const lines = frame.split('\n');
      for (const line of lines) {
        if (line.startsWith(':')) continue; // ": ping" keepalive / comment
        if (line.startsWith('event:')) {
          if (line.slice('event:'.length).trim() === 'end') {
            endedRef.current = true;
          }
          continue;
        }
        if (line.startsWith('data:')) {
          const payload = line.slice('data:'.length).replace(/^ /, '');
          try {
            const parsed = JSON.parse(payload);
            if (parsed && typeof parsed.b64 === 'string') {
              writeBytes(parsed.b64);
            }
          } catch {
            /* ignore malformed data frames */
          }
        }
      }
    };

    // Owned by THIS effect run, not shared refs. The mount effect resets
    // disposedRef/startedRef, so under StrictMode's double-invoke the first
    // loop was revived and two loops wrote into the same terminal.
    let cancelled = false;

    const runStream = async () => {
      let attempt = 0;
      const url = `${config.API_BASE_URL}/evaluations/${competitionId}/practice/terminals/${taskId}/${run}`;

      while (!cancelled && !disposedRef.current && !endedRef.current) {
        const controller = new AbortController();
        abortRef.current = controller;

        try {
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` },
            signal: controller.signal,
          });
          if (response.status === 404) {
            // This lane's run has not opened yet (sandbox still booting, or an
            // earlier run of a later lane). Not an error: wait and retry
            // without spending the reconnect budget, and leave the terminal's
            // contents alone.
            if (jobTerminalRef.current) break; // job over and it never opened
            await sleep(WAIT_RETRY_MS);
            continue;
          }
          if (!response.ok || !response.body) {
            throw new Error(`terminal stream responded ${response.status}`);
          }
          // Reset only AFTER a confirmed 200. The server replays its full
          // backlog on every connect, so a reset here prevents duplication --
          // but doing it before the fetch (as this used to) wiped the cell on
          // every failed connect, including the routine 404 above. That was the
          // blanking bug.
          // The cache is reset with the terminal, for the same reason: the
          // server replays its whole backlog on every connect, so keeping the
          // old bytes would store the run twice and replay it doubled.
          TRANSCRIPTS.delete(transcriptKey(jobId, taskId, run));
          termRef.current?.reset();
          if (!disposedRef.current) {
            setDotStatus('running');
            setReconnecting(false);
            setHasStreamed(true);
          }
          attempt = 0; // a successful connect resets the backoff budget

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let sep = buffer.indexOf('\n\n');
            while (sep !== -1) {
              const frame = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);
              processFrame(frame);
              sep = buffer.indexOf('\n\n');
            }
            if (endedRef.current) break;
          }
        } catch {
          if (disposedRef.current) return;
          // An abort from THIS effect's cleanup is not a connection failure --
          // a fresh loop is already taking over. Announcing "reconnecting" for
          // it would be a lie the user reads as breakage.
          if (cancelled) return;
          // fall through to the reconnect/backoff logic below
        }

        if (cancelled || endedRef.current || disposedRef.current) break;
        // The job finished while we were disconnected: the pod is gone, so there
        // is nothing left to reconnect to. Stop quietly and keep the transcript.
        if (jobTerminalRef.current) break;

        // Reconnect forever while the job is alive. There is no failure state
        // here by design: the server replays a lane's ENTIRE backlog on every
        // connect and the cell resets before writing it, so a reconnection
        // catches up completely -- nothing is lost by waiting. Giving up after
        // five tries only ever produced a dead cell that a page reload fixed.
        // The loop still exits the moment the job goes terminal, just above.
        attempt += 1;
        if (!disposedRef.current) setReconnecting(true);
        await sleep(attempt > MAX_RECONNECT_TRIES ? RECONNECT_SLOW_MS : RECONNECT_DELAY_MS);
      }

      if (endedRef.current && !disposedRef.current) {
        setDotStatus('done');
      }
      abortRef.current?.abort();
    };

    runStream();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
      // Release the re-entry guard too. This effect re-runs whenever
      // streamStatus changes, and the guard above exists only to stop TWO
      // loops in one effect instance -- so cancelling without clearing it
      // meant the cleanup killed the live loop and the very next run returned
      // at the guard, leaving every terminal stuck on "reconnecting..." with
      // nothing retrying. StrictMode is still safe: the cancelled loop exits
      // on its own closure flag, so exactly one loop is ever live.
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamStatus, competitionId, taskId, run]);

  // Class suffix mirrors dotStatus ('waiting' | 'running' | 'done'); the label
  // reads "live" while the feed is streaming so the cell feels real-time.
  // Once the feed has ended, pin 'done': a later poll that reports no stream
  // (the pod is gone) must not drag the cell back to 'waiting'.
  const effectiveStatus = endedRef.current || (jobTerminal && hasStreamed) ? 'done' : dotStatus;
  const dotClass = `lt-dot lt-dot--${effectiveStatus}`;
  const dotLabel = effectiveStatus === 'running' ? 'live' : effectiveStatus;
  const live = effectiveStatus === 'running';

  // A solid cursor that blinks while the lane runs (CSS), a hollow one otherwise, so live reads apart from finished at a glance.
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    term.options.cursorInactiveStyle = live ? 'block' : 'outline';
    term.refresh(0, term.rows - 1);
  }, [live]);

  return (
    <>
    <div className={`lt-cell${expanded ? ' lt-cell--expanded' : ''}${
      selected ? ' lt-cell--selected' : ''}${live ? ' lt-cell--live' : ''}`}>
      <div className="lt-cell__header">
        <span className="lt-cell__label">{label}</span>
        {usage && (usage.input_tokens > 0 || usage.output_tokens > 0) && (
          <span
            className="lt-cell__tokens"
            title={`New tokens this lane has sent and received, over ${usage.requests} `
              + `model call(s). Re-sent conversation is not counted twice.`}
          >
            <span className="lt-cell__tok">&#8593;{fmtTokens(usage.input_tokens)}</span>
            <span className="lt-cell__tok">&#8595;{fmtTokens(usage.output_tokens)}</span>
          </span>
        )}
        {search !== null && (
          <span className="lt-cell__search">
            <input
              autoFocus
              value={search}
              placeholder="Search this lane"
              aria-label="Search this lane"
              onChange={(e) => {
                setSearch(e.target.value);
                find(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') find(search, e.shiftKey);
                else if (e.key === 'Escape') {
                  // Escape closes the search first; a second one collapses the lane.
                  e.stopPropagation();
                  setSearch(null);
                  setHits(null);
                  searchRef.current?.clearDecorations();
                }
              }}
            />
            {search && hits && (
              <span className="lt-cell__hits">{hits.count ? `${hits.index + 1}/${hits.count}` : 'no match'}</span>
            )}
          </span>
        )}
        <span className="lt-cell__status">
          <span className={dotClass} aria-hidden="true" />
          <span className="lt-cell__status-text">{dotLabel}</span>
          {expanded && search === null && (
            <button
              type="button"
              className="lt-cell__expand"
              onClick={() => setSearch('')}
              title="Search (Ctrl/Cmd+F)"
              aria-label="Search this lane"
            >
              &#8981;
            </button>
          )}
          <button
            type="button"
            className="lt-cell__expand"
            onClick={() => onExpand(!expanded)}
            title={expanded ? 'Collapse (Esc), ← → other lanes' : 'Expand'}
            aria-label={expanded ? 'Collapse terminal' : 'Expand terminal'}
          >
            {expanded ? '✕' : '⤢'}
          </button>
        </span>
      </div>
      <div className="lt-cell__body">
        <div className="lt-cell__term" ref={containerRef} />
        {/* Only before this cell has ever streamed. The overlay is absolutely
            positioned over the whole body, so keying it off `!streamStatus`
            covered the finished transcript once the pod went away. */}
        {!hasStreamed && !streamStatus && (
          <div className="lt-cell__placeholder">
            {!jobTerminal
              ? 'waiting for lane...'
              // Once the pod is gone there is nothing to re-fetch, so a lane
              // this tab never watched has no transcript -- which is not the
              // same as never having run. Saying so was simply false for a
              // lane that completed while the user was on another page.
              : 'not buffered in this tab - download this run\'s logs'}
          </div>
        )}
        {reconnecting && !jobTerminal && (
          <div className="lt-cell__notice">reconnecting...</div>
        )}
        {behind && (
          <button type="button" className="lt-cell__latest" onClick={() => termRef.current?.scrollToBottom()}>
            &#8595; new output
          </button>
        )}
      </div>
    </div>
    {/* A click outside the expanded lane collapses it, without reaching the page underneath. */}
    {expanded && <div className="lt-backdrop" onClick={() => onExpand(false)} />}
    </>
  );
};

/** A terminal-shaped pane for a task with no run behind it.
 *
 *  Deliberately NOT a TerminalCell with an empty feed: that component opens an
 *  SSE stream on mount and retries a 404 every few seconds forever, so using it
 *  here would have every logged-in user on the page hammering a job id that
 *  does not exist. This is plain markup -- it renders on first paint, costs
 *  nothing, and shows what the pane is for.
 */
/**
 * Graded runs: status without output.
 *
 * A graded run executes on the HELD-OUT world, so nothing it produced may be
 * shown -- the agent's stdout quotes eval tickets, names and messages
 * verbatim. But a participant who just spent one of a small number of graded
 * submissions deserves to see that something is happening, and the terminals
 * card vanishing under them was the complaint this answers.
 *
 * What is safe, and why the line falls where it does:
 *   state, queue position  properties of the cluster, not of the eval world.
 *   elapsed time           observable anyway -- the finish is visible in real
 *                          time whether or not we render a clock.
 * Withheld deliberately:
 *   per-lane progress      the agent controls its own runtime, so a per-task
 *                          indicator is a covert channel: an agent can stall
 *                          conditionally and spell out eval facts through it.
 *   token counters         a number the agent controls directly. Same channel,
 *                          higher bandwidth.
 *   the score              with a hidden leaderboard and a submission quota,
 *                          showing it turns graded runs into hill-climbing on
 *                          the held-out set, which is the whole thing the
 *                          quota exists to prevent.
 */
const GradedLockCard: React.FC<{
  state: string | null;
  queued: boolean;
  live: boolean;
  selectedLabel: string | null;
}> = ({ state, queued, live, selectedLabel }) => (
  <div className="lt-locked">
    <span className="lt-locked__lock" aria-hidden="true">&#128274;</span>
    <h3 className="lt-locked__title">Terminals are hidden for graded runs</h3>
    <p className="lt-locked__body">
      This run executes against the held-out evaluation world. Showing live
      output would reveal the scenarios being scored.{' '}
      {live
        ? 'Progress, timing and token counts above are live and unaffected.'
        : 'Progress, timing and token counts above are final.'}
    </p>
    {selectedLabel && <p className="lt-locked__stat">{selectedLabel}</p>}
    {live && (
      // The refund rule, stated where the decision is actually made. It is not
      // a detail: cancelling now is free, cancelling in a minute is not.
      <p className="lt-locked__policy">
        {queued
          ? 'Cancel now and this submission is given back - it has not started.'
          : 'It has started, so cancelling will NOT give this submission back.'}
      </p>
    )}
    {!live && state === 'succeeded' && (
      <p className="lt-locked__policy">
        Recorded. Your score appears on the leaderboard when the competition ends.
      </p>
    )}
  </div>
);

const IdleCell: React.FC<{ taskId: string; label: string; hint?: string; status?: string }> = ({
  taskId, label, hint, status,
}) => (
  <div className="lt-cell lt-cell--idle">
    <div className="lt-cell__header">
      <span className="lt-cell__label">{label}</span>
      <span className="lt-cell__status">
        <span className="lt-dot lt-dot--waiting" aria-hidden="true" />
        <span className="lt-cell__status-text">{status ?? 'idle'}</span>
      </span>
    </div>
    <div className="lt-cell__body">
      <pre className="lt-cell__idle-term">
        <span className="lt-idle-user">agent@sandbox</span>
        <span className="lt-idle-punct">:</span>
        <span className="lt-idle-path">~/{taskId}</span>
        <span className="lt-idle-punct">$ </span>
        <span className="lt-idle-caret" />
        {'\n\n'}
        <span className="lt-idle-hint">
          {hint ?? 'no run yet - upload a practice submission to watch it here'}
        </span>
      </pre>
    </div>
  </div>
);

export interface PracticeJobHint {
  job_id: string;
  n_runs: number;
}

interface LiveTerminalsProps {
  competitionId: number;
  /**
   * Seed from the upload response so the card renders instantly, in the same
   * render cycle as the upload, before the first poll comes back.
   */
  practiceJobHint?: PracticeJobHint | null;
  /**
   * Bump to force an immediate refetch even without a job id (e.g. after a 429
   * "a practice run is already running"), so the card surfaces the live run.
   */
  refetchSignal?: number;
  /**
   * Fired after a successful cancel so the upload area can clear its 429 warning
   * (a new practice run is allowed once the current one is canceled).
   */
  onRunCanceled?: () => void;
  /**
   * Fired whenever the pulsar job state changes (and once on first read). This
   * component already polls that state, so consumers get it for free instead of
   * opening a second request: the competition page uses it to disable
   * "Download run logs" mid-run and to refresh the practice-run history when a
   * run finishes.
   */
  onJobStateChange?: (state: string | null, terminal: boolean) => void;
  /**
   * Fired whenever a terminals response arrives, with its `supported` flag
   * (false when missing). The competition page shows run-log controls only
   * for competitions that support practice runs.
   */
  onSupportedChange?: (supported: boolean) => void;
}

type PillKind = 'idle' | 'queued' | 'starting' | 'running' | 'finished' | 'canceled' | 'failed';

const TERMINAL_STATES: { [key: string]: boolean } = {
  succeeded: true,
  failed: true,
  canceled: true,
};

// Pick the delay to the next listing poll from the freshest response, or null
// to stop polling entirely (terminal job with every lane already flushed).
const computeDelay = (terminals: TerminalsResponse | null, hasHint: boolean): number | null => {
  if (!terminals) {
    // No listing yet (or an old backend still 404ing). A hint means an upload
    // just happened and the sandbox is spinning up, so poll fast.
    return hasHint ? POLL_STARTING_MS : POLL_DISCOVER_MS;
  }
  if (!terminals.run) {
    // The card is up but the user has no run. Nothing is going to change until
    // they act, or until a run starts in another tab.
    return hasHint ? POLL_STARTING_MS : POLL_IDLE_MS;
  }
  const state = terminals.state;
  const anyRunning = terminals.streams.some((s) => s.status === 'running');
  if (state && TERMINAL_STATES[state]) {
    // Terminal job: drop to the resting cadence once the last lane has sent its
    // end event. Stopping outright contradicted POLL_IDLE_MS's promise that a
    // run started in another tab shows up within 15s -- after one finished run
    // this card went silent until a reload.
    return anyRunning ? POLL_LIVE_MS : POLL_IDLE_MS;
  }
  if (terminals.available && state === 'running') {
    return POLL_LIVE_MS; // live: pick up run 2..N streams as they open
  }
  return POLL_STARTING_MS; // queued / null / sandbox starting (available false)
};

const LiveTerminals: React.FC<LiveTerminalsProps> = ({
  competitionId,
  practiceJobHint,
  refetchSignal,
  onRunCanceled,
  onJobStateChange,
  onSupportedChange,
}) => {
  const [terminals, setTerminals] = useState<TerminalsResponse | null>(null);
  const [currentRun, setCurrentRun] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  // The orange outline answers a click on a lane in the grid; the next click anywhere else clears it.
  const [picked, setPicked] = useState(false);
  useEffect(() => {
    if (!picked) return;
    const clear = (e: PointerEvent) => {
      if (!(e.target instanceof Element && e.target.closest('.rcp-lane'))) setPicked(false);
    };
    document.addEventListener('pointerdown', clear);
    return () => document.removeEventListener('pointerdown', clear);
  }, [picked]);
  // The maximized lane as "task/run", or null.
  const [expandedLane, setExpandedLane] = useState<string | null>(null);
  const moveExpanded = (step: number) => setExpandedLane((lane) => {
    if (!lane) return lane;
    const [task, run] = lane.split('/');
    const i = TASKS.findIndex((t) => t.id === task);
    return `${TASKS[(i + step + TASKS.length) % TASKS.length].id}/${run}`;
  });
  const hasUserNavigated = useRef(false);

  // Cancel-run control: a two-click arm/confirm (no browser confirm() dialog).
  const [cancelArmed, setCancelArmed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelNotice, setCancelNotice] = useState<string | null>(null);
  const cancelArmTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** Survives the pod going away; see nRuns below. */
  const lastKnownRuns = useRef(0);

  useEffect(
    () => () => {
      if (cancelArmTimerRef.current) clearTimeout(cancelArmTimerRef.current);
    },
    []
  );

  // Refs mirror the latest values so the self-scheduling poll loop can read them
  // without being torn down and recreated on every state change.
  const terminalsRef = useRef<TerminalsResponse | null>(null);
  const hintRef = useRef<PracticeJobHint | null>(practiceJobHint ?? null);
  const onSupportedRef = useRef(onSupportedChange);
  onSupportedRef.current = onSupportedChange;
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const loopRef = useRef<(() => void) | null>(null);
  const genRef = useRef(0);
  const triggerMountedRef = useRef(false);

  useEffect(() => {
    hintRef.current = practiceJobHint ?? null;
  }, [practiceJobHint?.job_id, practiceJobHint?.n_runs]);

  // Self-scheduling poll loop with an adaptive interval. Fetches once immediately
  // on mount, then re-arms with a delay derived from the job's latest state.
  useEffect(() => {
    if (!competitionId) return;
    // `active` is scoped to this effect instance so a stale in-flight loop from a
    // StrictMode double-invoke can never re-arm the timer after cleanup.
    let active = true;

    const fetchOnce = async () => {
      try {
        const response = await apiService.get(`/evaluations/${competitionId}/practice/terminals`, {
          headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` },
        });
        if (!active) return;
        // Keep every 200, including "you have no run": that payload is what
        // tells the card to render its idle state instead of vanishing.
        const payload = response.data as TerminalsResponse;
        if (payload && typeof payload === 'object') {
          terminalsRef.current = payload;
          setTerminals(payload);
        }
        onSupportedRef.current?.(Boolean(payload && typeof payload === 'object' && payload.supported));
      } catch (err: any) {
        if (!active) return;
        // 404 = no practice run: drop the listing. A seeded hint keeps the
        // "starting" card on screen (the render guard keys off the hint too).
        if (err?.status === 404) {
          terminalsRef.current = null;
          setTerminals(null);
        }
        // Other errors: keep the last listing and retry on the next tick.
      }
    };

    const schedule = () => {
      if (!active) return;
      const delay = computeDelay(terminalsRef.current, Boolean(hintRef.current));
      if (delay == null) return; // terminal + all lanes done: stop polling
      timerRef.current = setTimeout(loop, delay);
    };

    // A hidden tab is polling for nobody. This card now lives on every
    // competition page rather than only during a run, so background tabs would
    // otherwise be most of its cost. Resume with an immediate fetch, since the
    // first thing someone does on returning is look at it.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      } else {
        loopRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    // Mobile Safari restores a suspended page from bfcache without firing
    // visibilitychange, so without this the panel came back showing whatever it
    // last polled -- a phone asleep for half an hour still read three minutes.
    const onPageShow = () => loopRef.current?.();
    window.addEventListener('pageshow', onPageShow);

    const loop = async () => {
      if (!active || document.visibilityState === 'hidden') return;
      // A generation token guards against two loop() calls (e.g. the scheduled
      // tick racing a trigger-driven refetch) both re-arming the timer.
      const myGen = (genRef.current += 1);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      await fetchOnce();
      if (!active || genRef.current !== myGen) return;
      schedule();
    };

    loopRef.current = loop;
    loop(); // immediate first fetch

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      if (loopRef.current === loop) loopRef.current = null;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [competitionId]);

  // A new upload (hint) or a 429 conflict (refetchSignal) forces an instant
  // refetch. A brand-new job also clears the stale listing so the fresh
  // "starting" card shows at once instead of the previous run's terminals.
  useEffect(() => {
    if (!triggerMountedRef.current) {
      triggerMountedRef.current = true;
      return; // the main effect already did the first fetch on mount
    }
    const hintJob = practiceJobHint?.job_id;
    if (hintJob && terminalsRef.current && terminalsRef.current.job_id !== hintJob) {
      terminalsRef.current = null;
      setTerminals(null);
      setCurrentRun(null);
      hasUserNavigated.current = false;
      // The previous run's confirmation would otherwise stay pinned above the
      // card while the new one streams.
      setCancelNotice(null);
      lastKnownRuns.current = 0;
      forgetOtherJobs(hintJob);
    }
    loopRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceJobHint?.job_id, refetchSignal]);

  // A job is "known" once we have either a fetched listing or a seeded hint.
  const hint = practiceJobHint ?? null;
  const jobId = terminals?.job_id ?? hint?.job_id ?? null;
  // The caller's latest run of ANY kind. `jobId` above is narrower on purpose:
  // it is set only for a practice run, i.e. only when live terminals exist.
  const latestRun = terminals?.run ?? (hint ? { job_id: hint.job_id, kind: 'practice', state: 'queued' } : null);
  const jobState = terminals?.state ?? (hint ? 'queued' : null);
  const available = terminals?.available ?? false;
  // n_runs comes from the sandbox pod, so it disappears the moment the run
  // ends -- which collapsed the arrows to "Run 1 / 1" for a 3-run job and
  // snapped a user viewing run 2 back to run 1, right when they wanted to
  // page through the results. Remember the last real value.
  const nRuns = terminals?.n_runs || lastKnownRuns.current || hint?.n_runs || 0;
  const displayRuns = Math.max(nRuns, 1);
  useEffect(() => {
    if (nRuns > 0) lastKnownRuns.current = nRuns;
  }, [nRuns]);

  const jobTerminal = Boolean(jobState && TERMINAL_STATES[jobState]);
  const gradedRun = Boolean(latestRun && latestRun.kind !== 'practice');
  const queuePosition = terminals?.queue_position ?? null;
  const queueLength = terminals?.queue_length ?? 0;
  const usage = terminals?.usage ?? null;
  const progress = terminals?.progress ?? null;
  // ONE interpretation of the payload, shared by the panel, the lane grid
  // and the terminal area. They used to each derive their own, which is how
  // the strip said "running" while the terminals said "starting sandbox".
  const phase = derivePhase(jobState, progress?.lanes ?? []);

  // Report state changes upward (see onJobStateChange). Deduped on the value so
  // consumers are not re-notified on every poll tick.
  const lastReportedState = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastReportedState.current === jobState) return;
    lastReportedState.current = jobState;
    onJobStateChange?.(jobState, Boolean(jobState && TERMINAL_STATES[jobState]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobState]);

  // Default to run 1 and stay there. Every repeat now runs concurrently, so
  // "the highest run with a stream" is just "all of them" a second in -- it
  // would jump the view off run 1 the moment the sandbox came up.
  useEffect(() => {
    if (!jobId) {
      setCurrentRun(null);
      return;
    }
    setCurrentRun((prev) => {
      if (prev == null) return 1;
      if (prev > displayRuns) return Math.max(1, displayRuns);
      return prev;
    });
  }, [terminals, jobId, displayRuns]);

  // Keyed by lane, not by the visible run: every run is mounted now, so each
  // cell has to be able to find its own status.
  const streamStatus = useMemo(() => {
    const map = new Map<string, StreamStatus>();
    for (const stream of terminals?.streams ?? []) {
      map.set(`${stream.task_id}/${stream.run}`, stream.status);
    }
    return map;
  }, [terminals]);

  // Render as soon as we know this competition HAS a sandbox -- not once the
  // user happens to have a run in it. An idle grid of prompts is the honest
  // answer to "you have not run anything yet"; showing nothing at all was
  // indistinguishable from the page being broken, which is exactly how it read.
  // `supported` is undefined against an older backend, so fall back to the old
  // behaviour rather than rendering an empty card everywhere.
  if (!(terminals?.supported ?? false) && !hint) {
    return null;
  }

  const activeRun = currentRun ?? 1;
  const handleSelectLane = (task: string, run: number) => {
    hasUserNavigated.current = true;
    setSelectedTask(task);
    setCurrentRun(run);
    setPicked(true);
  };

  const canCancel = isLive(phase);

  const disarmCancel = () => {
    if (cancelArmTimerRef.current) {
      clearTimeout(cancelArmTimerRef.current);
      cancelArmTimerRef.current = undefined;
    }
    setCancelArmed(false);
  };

  const handleCancelClick = async () => {
    // First click arms the button for ~3s; a second click within that window fires.
    if (!cancelArmed) {
      setCancelArmed(true);
      cancelArmTimerRef.current = setTimeout(() => {
        setCancelArmed(false);
        cancelArmTimerRef.current = undefined;
      }, 3000);
      return;
    }
    disarmCancel();
    setCancelling(true);
    setCancelNotice(null);
    try {
      const response = await apiService.post(`/evaluations/${competitionId}/practice/cancel`, {}, {
        headers: { Authorization: `Bearer ${authUtils.getStoredToken()}` },
      });
      const body = response.data as {
        job_id?: string;
        state?: string;
        canceled?: boolean;
        message?: string;
      };
      setCancelNotice(body?.message ?? 'Run canceled.');
      if (body?.canceled) {
        // Flip the pill to "canceled" immediately. The next poll now CONFIRMS
        // this rather than fighting it: the job runner latches terminal states
        // for this task, so a cancel can no longer come back as running, or as
        // failed a few seconds later.
        const base = terminalsRef.current;
        const next: TerminalsResponse = {
          supported: true,
          job_id: base?.job_id ?? null,
          run: base?.run
            ? { ...base.run, state: 'canceled' }
            : { job_id: body.job_id ?? jobId ?? '', kind: 'practice', state: 'canceled' },
          state: 'canceled',
          available: false,
          n_runs: base?.n_runs ?? nRuns,
          // Drop the stale stream list: leaving `running` entries in it kept the
          // poll loop alive at its live cadence against a job that is over.
          streams: [],
          usage: base?.usage ?? null,
          progress: base?.progress ?? null,
          queue_position: null,
          queue_length: base?.queue_length ?? 0,
          queue_head_age_seconds: base?.queue_head_age_seconds ?? null,
        };
        terminalsRef.current = next;
        setTerminals(next);
        onRunCanceled?.(); // let the upload area clear its 429 warning
      }
    } catch (err: any) {
      // 502 (or anything else): surface the message and leave the button usable.
      setCancelNotice(err?.message ?? 'Cancel failed; try again');
    } finally {
      setCancelling(false);
    }
  };

  // The four lanes ON SCREEN, not every lane in the job. usage.total covers
  // all 4 x N lanes, so with parallel runs the header was three times the sum
  // of the four chips beneath it while claiming they added up to it.
  const shownLanes = TASKS.map((t) => usage?.per_lane?.[`${t.id}/${activeRun}`]);
  const totalIn = shownLanes.reduce((n, u) => n + (u?.input_tokens ?? 0), 0);
  const totalOut = shownLanes.reduce((n, u) => n + (u?.output_tokens ?? 0), 0);
  const shownCalls = shownLanes.reduce((n, u) => n + (u?.requests ?? 0), 0);

  const selected = { task: selectedTask ?? TASKS[0].id, run: activeRun };
  const selectedLane = progress?.lanes?.find(
    (l) => l.task === selected.task && l.run === selected.run);
  const selectedLabel = selectedLane
    ? `Selected lane \u00b7 ${selected.task.replace(/^\d+_/, '').replace(/_/g, ' ')} run ${
        selected.run}: ${selectedLane.state}` +
      (selectedLane.usage
        ? ` \u00b7 ${selectedLane.usage.input_tokens.toLocaleString()} new tok \u00b7 ${
            selectedLane.usage.requests} requests`
        : '')
    : phase === 'starting'
      ? 'Sandbox starting - lanes appear here as they launch.'
      : null;

  // Every run stays MOUNTED, not just the visible one: switching runs used to
  // unmount four cells and reconnect four streams, which flashed and lost the
  // scrollback. Hidden runs get `visibility: hidden` rather than
  // `display: none`, so the terminals keep their geometry and fit() stays valid.
  const mountedRuns = jobId
    ? Array.from({ length: displayRuns }, (_, i) => i + 1)
    : [1];

  // Locked only while a graded run is actually LIVE. Keying this off "the
  // latest run happens to be graded" made the lock permanent: submit graded
  // once and the terminals stayed dark forever afterwards, including with no
  // run at all. A finished graded run goes back to the ordinary idle panes.
  const gradedLocked = gradedRun && isLive(phase);

  const cancelButton = canCancel ? (
    <button
      type="button"
      className={`lt-cancel-btn${cancelArmed ? ' lt-cancel-btn--armed' : ''}`}
      onClick={handleCancelClick}
      disabled={cancelling}
      aria-label={gradedRun ? 'Cancel graded run' : 'Cancel practice run'}
    >
      {cancelling
        ? 'Canceling...'
        : cancelArmed
          ? 'Really cancel?'
          : gradedRun
            ? 'Cancel graded run'
            : 'Cancel run'}
    </button>
  ) : null;

  return (
    <section className="live-terminals" data-job-id={jobId ?? undefined} data-tour="live-terminals">
      <RunControlPanel
        phase={phase}
        progress={progress}
        mode={gradedRun ? 'graded' : 'practice'}
        nRuns={displayRuns}
        queuePosition={queuePosition}
        queueLength={queueLength}
        selected={selected}
        onSelectLane={handleSelectLane}
        tasks={TASKS}
        action={cancelButton}
      />

      {cancelNotice && <p className="lt-cancel-notice">{cancelNotice}</p>}

      {gradedLocked ? (
        <>
          <GradedLockCard
            state={jobState}
            queued={phase === 'queued'}
            live={isLive(phase)}
            selectedLabel={selectedLabel}
          />
          {/* The panes stay on screen, greyed and unconnected. Replacing them
              with a single card made the page change shape between a practice
              and a graded run; the point is that only their CONTENT is
              withheld. No TerminalCell here, so there is no stream at all. */}
          <div className="live-terminals__grid live-terminals__grid--locked" aria-hidden="true">
            {TASKS.map((task) => (
              <IdleCell
                key={`graded-${task.id}`}
                taskId={task.id}
                label={task.label}
                status="hidden"
                hint="output withheld while this runs on the held-out world"
              />
            ))}
          </div>
        </>
      ) : (
        <div className="live-terminals__runs">
          {mountedRuns.map((run) => (
            <div
              key={run}
              className={`live-terminals__grid${
                run === activeRun ? '' : ' live-terminals__grid--hidden'}`}
              aria-hidden={run !== activeRun}
            >
              {TASKS.map((task) =>
                jobId ? (
                  <TerminalCell
                    // Identity is (job, task, run). Without the job id,
                    // submitting a second time while viewing run 1 reused the
                    // same cell: no remount, no reset, and the PREVIOUS
                    // submission's transcript sat there through the new run.
                    key={`${jobId}-${task.id}-${run}`}
                    competitionId={competitionId}
                    jobId={jobId}
                    taskId={task.id}
                    run={run}
                    label={task.label}
                    streamStatus={streamStatus.get(`${task.id}/${run}`)}
                    jobTerminal={jobTerminal}
                    selected={picked && selected.task === task.id && selected.run === run}
                    expanded={expandedLane === `${task.id}/${run}`}
                    onExpand={(open) => setExpandedLane(open ? `${task.id}/${run}` : null)}
                    onMove={moveExpanded}
                  />
                ) : (
                  // No run: a static pane, NOT a TerminalCell. Mounting the
                  // real cell would start a stream loop that retries against a
                  // job that does not exist, forever, on every page view.
                  <IdleCell
                    key={`idle-${task.id}`}
                    taskId={task.id}
                    label={task.label}
                    hint={
                      gradedRun
                        ? "graded run finished - its output stays on the held-out world"
                        : undefined
                    }
                  />
                )
              )}
            </div>
          ))}
        </div>
      )}

      {!gradedRun && !latestRun && queueLength > 0 && (
        <p className="live-terminals__ended">
          {queueLength} run{queueLength === 1 ? '' : 's'} waiting in the cluster
          queue right now.
        </p>
      )}

      {jobTerminal && !gradedRun && (
        <p className="live-terminals__ended">
          Live feed ended - the sandbox has been torn down. The full
          transcript for every task and run is in this run&apos;s logs.
        </p>
      )}
    </section>
  );
};

export default LiveTerminals;
